import DropDownButton from 'components/DropDownButton'
import { useQuery } from 'react-query'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { EXAMS } from 'services'
import BookletDetailPage from './BookletDetailPage'

/**
 * External flow wrapper for the booklet detail view.
 *
 * Fetches booklet data via `getBookletDetail({ bundleId, studExternalId })`.
 * Builds a 4-level breadcrumb trail with external paths and passes it to
 * `BookletDetailPage`. Also computes available download links (question paper,
 * answer sheet, paper schema, eval-diff report) and injects them as
 * `headerActions`.
 */
export default function AdminBookletDetailPage() {
  const { examGroupId, bundleId, studExternalId } = useParams()

  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-booklet-detail', bundleId, studExternalId],
    () =>
      EXAMS.adminBundleEvaluation.getBookletDetail({
        bundleId: Number(bundleId),
        studExternalId: Number(studExternalId),
      }),
    { enabled: !!bundleId && !!studExternalId },
  )

  const uploads: { type?: string; originalType?: string; fileUrl?: string }[] =
    data?.paperUploads || []
  const questionPaper = uploads.find(
    (u) => u.type === 'questionPaper' || u.originalType === 'question_paper',
  )
  const answerSheet = uploads.find(
    (u) => u.type === 'answerKey' || u.originalType === 'answer_sheet',
  )
  const paperSchema = uploads.find(
    (u) => u.type === 'paperSchema' || u.originalType === 'paper_schema',
  )

  const hasDownloads =
    questionPaper?.fileUrl ||
    answerSheet?.fileUrl ||
    paperSchema?.fileUrl ||
    (data?.evaluators?.length ?? 0) > 0

  const evalDiffReportUrl = data
    ? `/print/booklet-eval-diff-report?bundleId=${bundleId}&studExternalId=${studExternalId}`
    : ''
  const scrutinizerReportUrl = data
    ? `/print/subject-bundle-scrutiny-report?bundleId=${bundleId}`
    : ''
  const evalPctDiffReportUrl = data
    ? `/print/subject-bundle-evaluation-difference-report?bundleId=${bundleId}`
    : ''
  const hasScrutinizer = (data?.evaluators || []).some(
    (e: { evaluatorType: string }) => e.evaluatorType === 'Scrutinizer',
  )

  return (
    <BookletDetailPage
      breadcrumbs={[
        { name: 'Exams', to: '/admin/examinations' },
        {
          name: data?.bundle?.examGroup?.groupName || 'Examinations',
          to: `/admin/examinations/${examGroupId}/subjects`,
        },
        {
          name: data?.bundle?.subjectCode || 'Subject',
          to: `/admin/examinations/${examGroupId}/subjects/bundles`,
        },
        {
          name: data?.bundle?.bundleSerialNo || 'Bundle',
          to: `/admin/examinations/${examGroupId}/bundles/${bundleId}/booklets`,
        },
        { name: data?.booklet?.barcode || `${studExternalId}`, to: null },
      ]}
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRefetch={refetch}
      headerActions={
        hasDownloads ? (
          <DropDownButton
            button={{
              label: 'Downloads',
              buttonProps: { variant: 'outlined', size: 'small' },
            }}
            menu={[
              ...(questionPaper?.fileUrl
                ? [
                    {
                      label: 'View Question Paper',
                      actionType: 'normal' as const,
                      onClick: () =>
                        window.open(questionPaper.fileUrl, '_blank'),
                    },
                  ]
                : []),
              ...(answerSheet?.fileUrl
                ? [
                    {
                      label: 'View Answer Sheet',
                      actionType: 'normal' as const,
                      onClick: () => window.open(answerSheet.fileUrl, '_blank'),
                    },
                  ]
                : []),
              ...(paperSchema?.fileUrl
                ? [
                    {
                      label: 'View Schema of Evaluation',
                      actionType: 'normal' as const,
                      onClick: () => window.open(paperSchema.fileUrl, '_blank'),
                    },
                  ]
                : []),
              {
                label: 'Evaluation Difference Report',
                actionType: 'link' as const,
                link: { to: evalDiffReportUrl, target: '_blank' },
              },
              ...(hasScrutinizer && scrutinizerReportUrl
                ? [
                    {
                      label: 'Scrutinizer Report',
                      actionType: 'link' as const,
                      link: {
                        to: scrutinizerReportUrl,
                        target: '_blank' as const,
                      },
                    },
                  ]
                : []),
              ...(evalPctDiffReportUrl
                ? [
                    {
                      label: 'Evaluation Percentage Difference Report',
                      actionType: 'link' as const,
                      link: {
                        to: evalPctDiffReportUrl,
                        target: '_blank' as const,
                      },
                    },
                  ]
                : []),
            ]}
          />
        ) : null
      }
    />
  )
}

/**
 * Internal flow wrapper for the booklet detail view.
 *
 * Fetches booklet data via `getInternalBookletDetail({ evaluationId,
 * evaluationBundleId, evaluationBookletId })`. Builds a 4-level breadcrumb
 * trail with internal paths. No `headerActions` — internal booklets have no
 * downloadable paper uploads.
 */
export function AdminInternalBookletDetailPage() {
  const { examGroupId, evaluationBundleId, evaluationBookletId } = useParams()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const evaluationId =
    location.state?.evaluationId || searchParams.get('evaluationId') || ''

  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-internal-booklet-detail', evaluationBookletId],
    () =>
      EXAMS.adminBundleEvaluation.getInternalBookletDetail({
        evaluationId,
        evaluationBundleId: evaluationBundleId || '',
        evaluationBookletId: evaluationBookletId || '',
      }),
    {
      enabled: !!evaluationId && !!evaluationBundleId && !!evaluationBookletId,
    },
  )

  return (
    <BookletDetailPage
      breadcrumbs={[
        { name: 'Exams', to: '/admin/examinations' },
        {
          name: 'Examination',
          to: `/admin/internal/${examGroupId}/subjects`,
        },
        {
          name: data?.bundle?.bundleSerialNo || 'Bundle',
          to: `/admin/internal/${examGroupId}/bundles/${evaluationBundleId}/booklets?evaluationId=${encodeURIComponent(
            evaluationId,
          )}`,
        },
        { name: data?.booklet?.barcode || 'Booklet', to: null },
      ]}
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRefetch={refetch}
    />
  )
}
